import React from 'react';
import { MultiWebClientMerchantModel, MultiWebClientSubItemModel } from '@/User/domain';
import { MasterMerchantItem } from '../atoms/MasterMerchantItem';
import { MasterSubItemList } from './MasterSubItemList';
import { DROPDOWN_STYLES } from '../constants/masterStyles';

interface MerchantListProps {
  merchants: MultiWebClientMerchantModel[];
  hoveredMainKey: string | null;
  hoveredSubKey: string | null;
  expandedKeys: Set<number>;
  onMerchantClick: (merchant: MultiWebClientMerchantModel) => void;
  onSubItemClick: (subItem: MultiWebClientSubItemModel) => void;
  onMerchantMouseEnter: (key: string) => void;
  onMerchantMouseLeave: () => void;
  onSubItemMouseEnter: (key: string) => void;
  onSubItemMouseLeave: () => void;
  onToggleExpand: (code: number) => void;
}

/**
 * Componente molecular que renderiza la lista completa de comercios y sus sub-items.
 * Gestiona la interacción de hover y clic para cada elemento.
 * 
 * @param merchants - Lista de comercios a mostrar
 * @param hoveredMainKey - Clave del comercio actualmente hovereado
 * @param hoveredSubKey - Clave del sub-item actualmente hovereado
 * @param onMerchantClick - Función para manejar el clic en un comercio
 * @param onSubItemClick - Función para manejar el clic en un sub-item
 * @param onMerchantMouseEnter - Función para manejar el hover sobre un comercio
 * @param onMerchantMouseLeave - Función para manejar cuando sale del hover de un comercio
 * @param onSubItemMouseEnter - Función para manejar el hover sobre un sub-item
 * @param onSubItemMouseLeave - Función para manejar cuando sale del hover de un sub-item
 */
export const MasterMerchantList: React.FC<MerchantListProps> = ({
  merchants,
  hoveredMainKey,
  hoveredSubKey,
  expandedKeys,
  onMerchantClick,
  onSubItemClick,
  onMerchantMouseEnter,
  onMerchantMouseLeave,
  onSubItemMouseEnter,
  onSubItemMouseLeave,
  onToggleExpand,
}) => {
  return (
    <div style={DROPDOWN_STYLES.merchantListContainer}>
      {merchants.map((merchant, index) => {
        const merchantKey = merchant.name + merchant.code;
        const hasSubItems = Array.isArray(merchant.subItems) && merchant.subItems.length > 0;
        const isFirst = index === 0;
        const isLast = index === merchants.length - 1;
        const isExpanded = expandedKeys.has(merchant.code);

        return (
          <div key={merchantKey + 'group'} style={DROPDOWN_STYLES.merchantListGroup}>
            <MasterMerchantItem
              merchant={merchant}
              isHovered={hoveredMainKey === merchantKey}
              isFirst={isFirst}
              isLast={isLast}
              hasSubItems={hasSubItems}
              arrow={hasSubItems ? (isExpanded ? 'up' : 'down') : undefined}
              onMouseEnter={() => onMerchantMouseEnter(merchantKey)}
              onMouseLeave={onMerchantMouseLeave}
              onClick={() => onMerchantClick(merchant)}
              onToggleExpand={() => {
                if (hasSubItems) {
                  onToggleExpand(merchant.code);
                }
              }}
            />
            {hasSubItems && isExpanded && (
              <MasterSubItemList
                subItems={merchant.subItems}
                hoveredSubKey={hoveredSubKey}
                onSubItemClick={onSubItemClick}
                onSubItemMouseEnter={onSubItemMouseEnter}
                onSubItemMouseLeave={onSubItemMouseLeave}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
